import posthog from 'posthog-js';

const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID || process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let gaInitialized = false;
let posthogInitialized = false;

function appendScript(src) {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) return existing;
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
  return script;
}

function simpleHash(value) {
  const str = String(value || '');
  if (!str) return '';
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return `u_${Math.abs(hash)}`;
}

function getUserId(user) {
  if (!user) return '';
  return user.id || user.userId || simpleHash(user.email);
}

function getUserProperties(extra = {}) {
  return {
    plan_type: extra.planType || 'basic',
    is_mobile: window.innerWidth <= 768,
  };
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function normalizeEventName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export function initAnalytics() {
  if (!isBrowser()) return;

  if (GA_MEASUREMENT_ID && !gaInitialized) {
    appendScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });
    gaInitialized = true;
  }

  if (POSTHOG_KEY && !posthogInitialized) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
    });
    posthogInitialized = true;
  }
}

export function setAnalyticsUser(user, extra = {}) {
  if (!isBrowser() || !user) return;
  const userId = getUserId(user);
  const userProps = getUserProperties(extra);

  if (gaInitialized && window.gtag && userId) {
    window.gtag('set', {
      user_id: userId,
      user_properties: userProps,
    });
  }

  if (posthogInitialized && userId) {
    posthog.identify(userId, {
      email: user.email || '',
      name: user.name || '',
      ...userProps,
    });
  }
}

export function clearAnalyticsUser() {
  if (!isBrowser()) return;
  if (gaInitialized && window.gtag) {
    window.gtag('set', { user_id: undefined });
  }
  if (posthogInitialized) {
    posthog.reset();
  }
}

export function trackPageView(path, title, extra = {}) {
  if (!isBrowser()) return;
  const pagePath = path || window.location.pathname;
  const pageTitle = title || document.title || 'RiskLo';
  const payload = {
    page_path: pagePath,
    page_title: pageTitle,
    ...extra,
  };

  if (gaInitialized && window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('event', 'page_view', {
      ...payload,
      send_to: GA_MEASUREMENT_ID,
    });
  }

  if (posthogInitialized) {
    posthog.capture('$pageview', payload);
  }
}

export function trackEvent(name, properties = {}) {
  if (!isBrowser()) return;
  const eventName = normalizeEventName(name);
  if (!eventName) return;
  const payload = { ...properties };

  if (gaInitialized && window.gtag) {
    window.gtag('event', eventName, payload);
  }

  if (posthogInitialized) {
    posthog.capture(eventName, payload);
  }
}

